import type { Crop } from '../../game-api';

/**
 * Pure maths + ordering behind the seed picker (rendered in planting-panel.ts).
 *
 * Kept apart from the DOM so the decisions a player actually makes — "which of
 * these is worth 30 minutes?" — are testable without a browser.
 */

/** Rounded-to-the-eye duration: seconds under a minute, h+m over an hour. */
export function fmtGrowth(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

/**
 * Picker order: everything the player can plant RIGHT NOW first, fastest first,
 * then the locked catalogue in the order it unlocks.
 *
 * The old picker sorted the whole list by unlockLevel, so the crops you own sat
 * interleaved with ones you cannot buy and the fast/slow split — the actual
 * choice being made — was invisible. Grow time is the primary key inside the
 * plantable group because that is what the player is committing the bed to.
 * Ties break on id so the order is stable across repaint (Array.sort is stable,
 * but the input array order is not guaranteed by the API).
 */
export function sortSeeds(crops: Crop[], level: number): Crop[] {
  return [...crops].sort((a, b) => {
    const la = a.unlockLevel > level ? 1 : 0, lb = b.unlockLevel > level ? 1 : 0;
    if (la !== lb) return la - lb;
    if (la) return a.unlockLevel - b.unlockLevel || a.growthMs - b.growthMs || a.id.localeCompare(b.id);
    return a.growthMs - b.growthMs || a.id.localeCompare(b.id);
  });
}

/**
 * Bar width as a percentage of the SLOWEST crop shown.
 *
 * A sorted list gives order but not ratio, and the ratio is the whole decision:
 * pumpkin at 30m against wheat at 2m is 15×, which a bar shows in one glance and
 * a list of numbers does not. Floor of 4% so the fastest crop still draws a
 * visible stub instead of nothing.
 */
export function growthBarPct(growthMs: number, slowestMs: number): number {
  if (!(slowestMs > 0)) return 100;
  return Math.max(4, Math.min(100, Math.round((growthMs / slowestMs) * 100)));
}

/** Slowest grow time in a set — the bar's denominator (0 for an empty set). */
export function slowestGrowth(crops: Crop[]): number {
  return crops.reduce((m, c) => Math.max(m, c.growthMs), 0);
}

/**
 * How far along a planted bed is, 0..1.
 *
 * An UNWATERED bed is pinned at 0 whatever its remaining time, because the
 * server does not tick it down — a dry seed does not grow at all, and drawing a
 * part-filled bar for one is the single most common reason a player believes the
 * game is stuck.
 */
export function growthFraction(remainingMs: number, growthMs: number, watered: boolean, ready = false): number {
  if (ready) return 1;
  if (!watered) return 0;
  if (!(growthMs > 0)) return 1;
  return Math.min(1, Math.max(0, 1 - remainingMs / growthMs));
}
